/** "Add rule" affordance: a popover hosting an always-open searchable
 *  Autocomplete grouped by domain — 45+ templates need type-ahead, not a
 *  Select. Picking an option appends the rule and closes the popover. */
import AddIcon from '@mui/icons-material/Add';
import { Autocomplete, Button, Popover, TextField } from '@mui/material';
import { useRef, useState } from 'react';

import { ADD_MENU_GROUPS, RULE_TEMPLATE_LABELS } from './ruleCatalog';

interface TplOption {
  tpl: number;
  label: string;
  group: string;
}

const OPTIONS: TplOption[] = ADD_MENU_GROUPS.flatMap((g) =>
  g.tpls.map((tpl) => ({ tpl, label: RULE_TEMPLATE_LABELS[tpl] ?? `#${tpl}`, group: g.label })),
);

export function AddRuleMenu({
  onPick,
  disabled,
}: {
  onPick: (tpl: number) => void;
  disabled?: boolean;
}) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        variant="contained"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        Add rule
      </Button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 1, width: 380 } } }}
      >
        {open && (
          <Autocomplete<TplOption>
            open
            autoHighlight
            options={OPTIONS}
            groupBy={(o) => o.group}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.tpl === b.tpl}
            onChange={(_, opt) => {
              if (opt) {
                onPick(opt.tpl);
                setOpen(false);
              }
            }}
            // The popover IS the dropdown — keep the listbox inline and tall.
            disablePortal
            slotProps={{ popper: { sx: { position: 'static !important', transform: 'none !important' } } }}
            ListboxProps={{ style: { maxHeight: 360 } }}
            renderInput={(params) => (
              <TextField {...params} autoFocus size="small" placeholder="Search conditions…" />
            )}
          />
        )}
      </Popover>
    </>
  );
}
